import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import Title from '../../../sharedComponents/Title/Title';
import useDataSuite from '../useDataSuite';
import { AutoSizer, Column, Table } from 'react-virtualized';
import { tableColors, Theme } from '../../../config/constants';
import { FaTrash, FaCircle, FaClipboardList } from 'react-icons/fa';
import { BsPencilSquare } from 'react-icons/bs';
import { IoMdInformationCircle } from "react-icons/io";
import { Tooltip } from "@mui/material";
import useDataSuiteWidgets from './useDataSuiteWidgets';
import { isEqual } from "lodash";
import EmptyDataMessage from '../../../sharedComponents/ErrorMessages/EmptyDataMessage';
import EmptySearchResultsMessage from '../../../sharedComponents/ErrorMessages/EmptySearchResultsMessage';

ChartJS.register(...registerables);

const SalesByCategoryWidget = (props) => {
    const navigate = useNavigate();
    const { w, i } = props;
    const {  onClickWidgetDrawerOpen, onClickDeleteWidget } = useDataSuite.useWidgetControls();
    const { widgetTitle, tableOptions, salesByCategoryBarData, salesByCategoryTableLabels, sortedDetailsTableData, filtersEnabled } = useDataSuiteWidgets.useSalesByCategoryWidget();
    const widgetOptionsOpen = useSelector(({ui}) => ui.slideDrawer);
    const selectedFilters = useSelector(({datasuite}) => datasuite.selectedFilters);
    const selectedDetailsFilters = useSelector(({datasuite}) => datasuite.selectedDetailsFilters);
    const dateString = useSelector(({dates}) => dates.datasuiteDateString);
    const detailsDateString = useSelector(({dates}) => dates.datasuiteDetailsDateString);
    const isLoading = useSelector(({datasuite}) => datasuite.isLoading.salesByCategoryLoading);
    const { getSalesByCategory } = useDataSuite.useDataSuite();
    const [isWidgetHovered, setIsWidgetHovered] = useState(false);

    const prevFilters = useRef(null);
    const prevDate = useRef(null);

    const handleMouseEnter = () => {
        setIsWidgetHovered(true);
    };
    const handleMouseLeave = () => {
        setIsWidgetHovered(false);
    };

    const onClickSeeDetails = () => {
        navigate({pathname: '/salesstream/details', search: "?Sales%20By%20Category"});
    };

    useEffect(() => {
        if (isEqual(selectedFilters, prevFilters.current) && isEqual(dateString, prevDate.current)) {
            getSalesByCategory(true);
        } else {
            getSalesByCategory(false);   
        }
        prevFilters.current = selectedFilters;
        prevDate.current = dateString;

    // eslint-disable-next-line
    }, [selectedFilters, selectedDetailsFilters, dateString, detailsDateString]);

    return (
        <Col onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{ position: 'relative'}} >
            <Row className='d-flex align-items-center'>
                <Col className='w-fc pr-0'>
                    <div className='d-flex align-items-center px-0'>
                    <Title title={widgetTitle}/>
                    <Tooltip title='Displays your sales by your relevant categories. Helpful for identifying category growth opportunities as well as verifying compliance data. Information is up-to-date and updated daily.' arrow>
                        <div className='pt-3 pb-2'>
                        <IoMdInformationCircle className="text-size-18 " style={{color: Theme().colors.accent}}/>
                        </div>
                    </Tooltip>
                    </div>
                </Col>
                {(widgetOptionsOpen || isWidgetHovered)  && ( <>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button 
                            block 
                            className='py-1 button-label replaceWidgetButton' 
                            size='sm' 
                            color='blueFilter'
                            onClick={() => onClickWidgetDrawerOpen("Replace Widget", true, i)}>
                            <BsPencilSquare color='white' /> 
                        </Button>
                    </Col>
                    <Col className='w-fc px-1 pt-3 pb-2'>
                        <Button
                            block 
                            className='py-1 button-label deleteWidgetButton' 
                            size='sm' 
                            color='redLight'
                            onClick={() => onClickDeleteWidget(w)}>
                            <FaTrash color='white' />
                        </Button>
                    </Col>
                </>
                )}
                <Col className={`w-fc me-3 ms-auto px-1 pt-3 pb-2`}>
                <Button
                    block
                    className={`py-1 button-label expandWidgetButton`}
                    size='sm'
                    outline={true}
                    color={Theme().colors.accentName}
                    onClick={() => onClickSeeDetails()}>
                    <FaClipboardList color={Theme().colors.accentName} style={{position: 'relative', bottom: '1px'}}/> See Details
                </Button>
                </Col>
            </Row>
            {(sortedDetailsTableData?.length <= 0) &&
            <Row className="mx-auto"style={{height: '70%'}}>
                <Col className="mx-auto my-auto" style={{width:"2.5%"}}>
                    {isLoading &&
                    <Spinner style={{marginLeft:'50%'}} color='beplBlue' animation="border" />
                    }
                    {(!isLoading && sortedDetailsTableData.length <= 0 && filtersEnabled) &&
                    <EmptySearchResultsMessage />
                    }
                    {(!isLoading && sortedDetailsTableData.length <= 0 && !filtersEnabled) &&
                    <EmptyDataMessage />
                    }
                </Col>
            </Row>
            }
            {(sortedDetailsTableData?.length > 0) &&
            <Row className="w-100 mb-3 mx-0" style={{minHeight: '315px'}}>
                <Col className="col-12 col-xl-8 p-0">
                    <Bar
                        data = {{
                            labels: salesByCategoryTableLabels, 
                            datasets: [
                                {
                                    label: '',
                                    data: salesByCategoryBarData,
                                    backgroundColor: tableColors
                                }
                            ]
                        }}
                        options = {tableOptions}
                    />
                </Col>
                <Col className="text-size-14 p-0 pe-3">
                    <Row style={{overflow: 'hidden', height: '100%'}}>
                        <AutoSizer>
                            {({width, height}) => (
                                <Table className="table salesByCategoryTable border-grayLight" id="salesByCategoryTable"
                                    width={width}
                                    height={height}
                                    headerHeight={34.5}
                                    rowHeight={30}
                                    rowCount={salesByCategoryTableLabels.length}
                                    rowGetter={({ index }) => salesByCategoryTableLabels[index]}
                                >
                                    <Column
                                    label='Category'
                                    dataKey='salesByCategoryTableLabels'
                                    width={width}
                                    cellRenderer={({cellData, rowIndex}) => (<span><FaCircle className='w-fc my-auto' style={{color: tableColors[rowIndex]}}/> {salesByCategoryTableLabels[rowIndex]}</span>)}
                                    />
                                </Table>
                            )}
                        </AutoSizer>
                    </Row>
                </Col>
            </Row>
            }
        </Col>
    );
    
}

export default SalesByCategoryWidget;